import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { updateMipKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import {
	createMIPPersonalCustomDetails,
	deleteCustomDetail,
	getMIPPersonalCustomDetails,
	updateMIPPersonalCustomDetails,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { CreateCustomDetailDto, DataCustomDetailTct } from '@dealstack/shared/lib/generated';
import { isEqual } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { CustomDetailDataTypeEnum } from '@dealstack/shared/lib/generated/models/CustomDetailDataTypeEnum.ts';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import {
	ILocalPersonalDataCustomDetails,
	PersonalDataCustomDetailsFormValues,
} from './PersonalDataTagManagerModal/PersonalDataTagManagerModal.types';

export interface IPredefinedCustomDetail {
	name: string;
	isChecked: boolean;
}

export enum PREDEFINED_CUSTOM_DETAIL {
	INDIVIDUAL_BANK = 0,
	INDIVIDUAL_TAX = 1,
	INDIVIDUAL_TIN = 2,
	INDIVIDUAL_PLACE_OF_BIRTH = 3,
	VEHICLE_BANK = 4,
	INDIVIDUAL_COUNTRY_OF_BIRTH = 5,
	INDIVIDUAL_CITIZENSHIP = 6,
	INDIVIDUAL_EMPLOYEE_ID = 7,
	VEHICLE_CONTROLLING_PARTY = 8,
}

export const usePersonalDataController = () => {
	const mipCreateController = useContext(MIPCreateContext);
	const mipId = mipCreateController?.mipId ?? '';

	const { data: kycSetting } = useMipKycSetting(mipId);

	const getCustomDetailsRequest = useWrappedRequest(getMIPPersonalCustomDetails);
	const createCustomDetailsRequest = useWrappedRequest(createMIPPersonalCustomDetails);
	const updateCustomDetailsRequest = useWrappedRequest(updateMIPPersonalCustomDetails);
	const deleteCustomDetailsRequest = useWrappedRequest(deleteCustomDetail);

	const [individualArray, setIndividualArray] = useState<ILocalPersonalDataCustomDetails[]>();
	const [vehiclesArray, setVehiclesArray] = useState<ILocalPersonalDataCustomDetails[]>();
	const [remoteCustomDetails, setRemoteCustomDetails] = useState<DataCustomDetailTct[]>();

	const [isPersonalModalVisible, setIsPersonalModalVisible] = useState(false);
	const [isVehicleModalVisible, setIsVehicleModalVisible] = useState(false);

	const tMipCreate = useTranslationMipCreate();

	const personalDataBackupData = mipCreateController?.dataBackup?.[MIP_CREATE_ROUTES.PERSONAL];

	const personalDetailsForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			fieldArray: {} as PersonalDataCustomDetailsFormValues | null,
			predefinedDetails: [] as IPredefinedCustomDetail[],
		},
	});
	personalDetailsForm.watch();

	const onTagManagerModalClose = () => {
		setIsPersonalModalVisible(false);
		setIsVehicleModalVisible(false);
	};

	const onOpenIndividualTagModal = () => {
		setIsPersonalModalVisible(true);
	};

	const onOpenVehicleTagModal = () => {
		setIsVehicleModalVisible(true);
	};

	const addLocalIds = (
		data: DataCustomDetailTct[] | undefined
	): ILocalPersonalDataCustomDetails[] => {
		if (data == null) {
			return [];
		} else {
			return data?.map((item) => {
				// * This if is required to unify all remote & locally added details in case of removeCustomDetailLocally method on mixed data before save draft
				if (item.id != null) {
					return {
						...item,
						localId: item.id,
					};
				} else {
					return {
						...item,
					};
				}
			});
		}
	};

	const prepareCustomDetailsData = () => {
		getCustomDetailsRequest.fetch().onSuccess((success) => {
			const filteredCustomDetails = success?.payload?.filter(
				(cd) => cd.dataType === CustomDetailDataTypeEnum.STRING
			);
			setRemoteCustomDetails(filteredCustomDetails);
			const array = addLocalIds(filteredCustomDetails);

			const individualArray = array?.filter((item) => item.type === 'INDIVIDUAL');
			setIndividualArray(individualArray);

			const vehicleArray = array?.filter((item) => item.type === 'VEHICLE');
			setVehiclesArray(vehicleArray);
		});
	};

	const prepareLocalPredefinedCustomDetails = () => {
		if (!kycSetting) {
			return;
		}

		const remotePredefinedArray: IPredefinedCustomDetail[] = [];
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_BANK] = {
			name: '',
			isChecked: kycSetting.isInvestorBankAccountRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TAX] = {
			name: '',
			isChecked: kycSetting.isTaxResidenceRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TIN] = {
			name: '',
			isChecked: kycSetting.isTinRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_PLACE_OF_BIRTH] = {
			name: '',
			isChecked: kycSetting.isPlaceOfBirthRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.VEHICLE_BANK] = {
			name: '',
			isChecked: kycSetting.isVehicleBankAccountRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_COUNTRY_OF_BIRTH] = {
			name: '',
			isChecked: kycSetting.isCountryOfBirthRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_EMPLOYEE_ID] = {
			name: '',
			isChecked: kycSetting.isEmployeeIdRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_CITIZENSHIP] = {
			name: '',
			isChecked: kycSetting.isCitizenshipRequired ?? false,
		};
		remotePredefinedArray[PREDEFINED_CUSTOM_DETAIL.VEHICLE_CONTROLLING_PARTY] = {
			name: '',
			isChecked: kycSetting.isControllingPartyInfoRequired ?? false,
		};

		personalDetailsForm.reset({ predefinedDetails: remotePredefinedArray });
	};

	useEffect(() => {
		if (personalDataBackupData) {
			setRemoteCustomDetails(personalDataBackupData.remoteCustomDetails);
			setIndividualArray(personalDataBackupData.individualArray);
			setVehiclesArray(personalDataBackupData.vehiclesArray);
			personalDetailsForm.reset({
				predefinedDetails: personalDataBackupData.predefinedDetails,
			});
			return;
		}

		prepareCustomDetailsData();
		prepareLocalPredefinedCustomDetails();
	}, [personalDataBackupData, kycSetting]);

	const sendRequests = async (): Promise<WizardSaveResult> => {
		let result: WizardSaveResult = SAVE_OK;
		const updatedCustomDetails = [...(individualArray || []), ...(vehiclesArray || [])];
		const newDetails = extractDetailsToCreate(updatedCustomDetails);
		const toDelete = extractDetailsToDelete(updatedCustomDetails);
		const toUpdate = extractDetailsToUpdate(updatedCustomDetails);

		mipCreateController?.setIsLoading(true);
		let isAnyFailed = false;

		const newDetailsPromise = (() => {
			return newDetails?.map((item) => {
				return createCustomDetailsRequest
					.fetch({
						name: item.name,
						instruction: item.instruction,
						type: item.type,
					} as CreateCustomDetailDto)
					.onAnyError(() => {
						isAnyFailed = true;
					});
			});
		})();
		const deleteDetailsPromise = (() => {
			return toDelete?.map((item) => {
				return deleteCustomDetailsRequest
					.fetch({
						customDetailId: item.id,
					})
					.onAnyError(() => {
						isAnyFailed = true;
					});
			});
		})();
		const updateDetailsPromise = (() => {
			return toUpdate?.map((item) => {
				return updateCustomDetailsRequest
					.fetch({
						customDetailId: item.id as string,
						name: item.name,
						instruction: item.instruction ?? '',
					})
					.onAnyError(() => {
						isAnyFailed = true;
					});
			});
		})();

		const predefinedValues = personalDetailsForm.getValues('predefinedDetails');
		const updatePredefinedDetails = updateMipKycSetting({
			isInvestorBankAccountRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_BANK]?.isChecked ?? false,
			isTaxResidenceRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TAX]?.isChecked ?? false,
			isTinRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TIN]?.isChecked ?? false,
			isPlaceOfBirthRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_PLACE_OF_BIRTH]?.isChecked ??
				false,
			isCountryOfBirthRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_COUNTRY_OF_BIRTH]?.isChecked ??
				false,
			isEmployeeIdRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_EMPLOYEE_ID]?.isChecked ??
				false,
			isCitizenshipRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_CITIZENSHIP]?.isChecked ??
				false,
			isVehicleBankAccountRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.VEHICLE_BANK]?.isChecked ?? false,
			isControllingPartyInfoRequired:
				predefinedValues[PREDEFINED_CUSTOM_DETAIL.VEHICLE_CONTROLLING_PARTY]?.isChecked ??
				false,
		}).catch(() => {
			isAnyFailed = true;
		});

		await Promise.allSettled([
			newDetailsPromise,
			deleteDetailsPromise,
			updateDetailsPromise,
			updatePredefinedDetails,
		]).then(() => {
			if (isAnyFailed) {
				mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.PERSONAL, {
					individualArray,
					vehiclesArray,
					remoteCustomDetails,
					predefinedDetails: personalDetailsForm.getValues(`predefinedDetails`),
				});
				showToastV2({
					title: tMipCreate('personal_data.general_error'),
					state: 'error',
				});
				result = SAVE_FAILED;
			} else {
				mipCreateController?.removeBackupData(MIP_CREATE_ROUTES.PERSONAL);
				prepareCustomDetailsData();
			}
		});
		mipCreateController?.setIsLoading(false);
		return result;
	};

	useWizardStep(sendRequests);

	const updateCustomDetailsLocally = (type: CustomDetailEnum) => {
		const fieldArray = personalDetailsForm.getValues('fieldArray')?.rows;
		const filteredArray = fieldArray?.filter((item) => item.name !== '');
		type === CustomDetailEnum.INDIVIDUAL
			? setIndividualArray(filteredArray)
			: setVehiclesArray(filteredArray);
		type === CustomDetailEnum.INDIVIDUAL
			? setIsPersonalModalVisible(false)
			: setIsVehicleModalVisible(false);
	};

	const removeCustomDetailLocally = (type: CustomDetailEnum, id: string | undefined) => {
		type === CustomDetailEnum.INDIVIDUAL
			? setIndividualArray(individualArray?.filter((item) => item.localId !== id))
			: setVehiclesArray(vehiclesArray?.filter((item) => item.localId !== id));
	};

	const extractDetailsToCreate = (array: ILocalPersonalDataCustomDetails[]) => {
		return array.filter((item) => item.id == null);
	};

	const extractDetailsToDelete = (array: ILocalPersonalDataCustomDetails[]) => {
		return remoteCustomDetails?.filter((item) =>
			array.every((updatedItem) => updatedItem.id !== item.id)
		);
	};

	const extractDetailsToUpdate = (array: ILocalPersonalDataCustomDetails[]) => {
		return array?.filter((localItem) => {
			const item = remoteCustomDetails?.find((remoteItem) => remoteItem.id === localItem.id);
			if (!item) {
				return false;
			}

			return !isEqual(
				{ name: item.name, instruction: item.instruction },
				{ name: localItem.name, instruction: localItem.instruction }
			);
		});
	};

	const onCheckboxClicked = (type: PREDEFINED_CUSTOM_DETAIL) => {
		personalDetailsForm.setValue(
			`predefinedDetails.${type}.isChecked`,
			!personalDetailsForm.getValues(`predefinedDetails.${type}.isChecked`),
			{ shouldDirty: true }
		);
	};

	const getCheckboxValue = (type: PREDEFINED_CUSTOM_DETAIL) => {
		return personalDetailsForm.getValues(`predefinedDetails.${type}.isChecked`);
	};

	const isTinChecked = personalDetailsForm.watch(
		`predefinedDetails.${PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TIN}.isChecked`
	);

	useEffect(() => {
		if (!isTinChecked) {
			return;
		}

		personalDetailsForm.setValue(
			`predefinedDetails.${PREDEFINED_CUSTOM_DETAIL.INDIVIDUAL_TAX}.isChecked`,
			true
		);
	}, [isTinChecked]);

	return {
		personalDetailsForm,
		isVehicleModalVisible,
		isPersonalModalVisible,
		onTagManagerModalClose,
		onOpenIndividualTagModal,
		onOpenVehicleTagModal,
		updateCustomDetailsLocally,
		removeCustomDetailLocally,
		individualArray,
		vehiclesArray,
		onCheckboxClicked,
		getCheckboxValue,
		isTinChecked,
	};
};
